/**
 * useSessionTimeout Hook
 * Monitors session expiration and provides warning state for timeout modal
 *
 * Features:
 * - Tracks time remaining until token expires
 * - Shows warning 5 minutes before expiration
 * - Updates countdown every second when warning is shown
 * - Provides extend session and logout actions
 * - Auto-logout when timer reaches zero
 */

import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { getAccessToken, getTokenExpiresIn, refreshAccessToken, clearTokens } from '@/utils/token';
import { getSessionOwner, ownsSession, onSessionReplacement } from '@/utils/session';
import { useAuthStore } from '@/store/auth.store';
import { buildLoginRedirectUrl } from '@/utils/returnTo';
import * as authApi from '@/api/auth.api';

/**
 * Time before expiration to show warning (5 minutes)
 */
const WARNING_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * Interval for checking token expiration (10 seconds)
 */
const CHECK_INTERVAL_MS = 10 * 1000;

/**
 * Interval for countdown updates when warning is shown (1 second)
 */
const COUNTDOWN_INTERVAL_MS = 1000;

/**
 * Return type for useSessionTimeout hook
 */
export interface SessionTimeoutState {
  /** Whether the warning modal should be shown */
  showWarning: boolean;
  /** Time remaining in milliseconds */
  timeRemaining: number;
  /** Formatted time remaining (MM:SS) */
  formattedTime: string;
  /** Extend the session by refreshing the token */
  extendSession: () => Promise<void>;
  /** Dismiss the warning (will reappear if still within threshold) */
  dismissWarning: () => void;
  /** Whether extend session is in progress */
  isExtending: boolean;
}

/**
 * Format milliseconds to MM:SS string
 */
const formatTime = (ms: number): string => {
  if (ms <= 0) return '0:00';

  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * useSessionTimeout Hook
 *
 * Monitors the access token expiration and manages the session timeout warning state.
 * Shows a warning 5 minutes before the token expires and counts down in real-time.
 *
 * @example
 * ```tsx
 * const {
 *   showWarning,
 *   timeRemaining,
 *   formattedTime,
 *   extendSession,
 *   isExtending,
 * } = useSessionTimeout();
 *
 * if (showWarning) {
 *   return <SessionTimeoutModal timeRemaining={formattedTime} onExtend={extendSession} />;
 * }
 * ```
 */
export const useSessionTimeout = (): SessionTimeoutState => {
  const { isAuthenticated, updateTokens, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isExtending, setIsExtending] = useState(false);

  // Track if we've already triggered auto-logout to prevent multiple calls
  const hasAutoLoggedOutRef = useRef(false);
  // Track if warning was manually dismissed
  const wasDismissedRef = useRef(false);
  useEffect(
    () =>
      onSessionReplacement(() => {
        hasAutoLoggedOutRef.current = false;
        wasDismissedRef.current = false;
        setShowWarning(false);
        setIsExtending(false);
        setTimeRemaining(0);
      }),
    []
  );

  // Keep the latest `location` and `navigate` in refs so
  // `doSessionExpiredLogout` can read them at invocation time WITHOUT making
  // them effect/callback deps. Both are unstable across navigations in
  // React Router v6 (`useNavigate`'s memo depends on the current pathname),
  // so listing them as deps would tear down and recreate the polling
  // interval on every route change — functionally harmless (an immediate
  // checkExpiration runs on each setup), but wasteful.
  //
  // Refs are updated via useLayoutEffect (not useEffect) to close a narrow
  // race: passive effects are scheduled as a separate task after commit, so
  // a setInterval callback already queued by the browser (e.g., backgrounded
  // tab returning to the foreground, immediate nav + imminent token expiry)
  // can run between the React commit and the passive-effect flush — reading
  // a stale locationRef and building returnTo from the PREVIOUS page.
  // useLayoutEffect runs synchronously in the commit phase before any
  // subsequent macrotask, so the ref is always fresh by the time setInterval
  // next fires. Mensova is a client-only SPA (Vite), so useLayoutEffect's
  // SSR warning is not applicable.
  const locationRef = useRef(location);
  const navigateRef = useRef(navigate);
  useLayoutEffect(() => {
    locationRef.current = location;
  }, [location]);
  useLayoutEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  /**
   * Full session-expired cleanup: clear tokens, reset auth store, drop all
   * cached queries (parity with useAuth.logout but without the snackbar —
   * the login-page banner is the user-visible signal), then SPA-navigate
   * to /login with returnTo + reason=expired. We use navigate() rather
   * than window.location.href to preserve the React Query / React Router
   * instances; the store reset plus cache clear is enough to scrub
   * per-user state.
   *
   * Call-sites: auto-logout branch (countdown hits zero) and the
   * extendSession catch path (refresh token rejected).
   *
   * `locationRef` and `navigateRef` are intentionally not in the dep array —
   * they're refs by design so route changes don't re-create this callback
   * (which would thrash the polling interval via the main effect's deps).
   */
  const doSessionExpiredLogout = useCallback(() => {
    clearTokens();
    logout();
    queryClient.clear();
    navigateRef.current(buildLoginRedirectUrl(locationRef.current, 'expired'), {
      replace: true,
    });
  }, [logout, queryClient]);

  /**
   * Calculate time remaining until token expires
   */
  const calculateTimeRemaining = useCallback((): number => {
    if (!isAuthenticated) return 0;

    const token = getAccessToken();
    if (!token) return 0;

    return getTokenExpiresIn(token);
  }, [isAuthenticated]);

  /**
   * Extend session by refreshing the token
   */
  const extendSession = useCallback(async () => {
    if (isExtending) return;

    const owner = getSessionOwner();
    setIsExtending(true);

    try {
      const tokens = await refreshAccessToken(async (refreshToken) => {
        const response = await authApi.refreshToken(refreshToken);
        return {
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
        };
      });

      if (!ownsSession(owner)) return;
      // Update store with new tokens
      updateTokens({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
      });

      // Hide warning and reset state
      setShowWarning(false);
      wasDismissedRef.current = false;
      hasAutoLoggedOutRef.current = false;
    } catch {
      if (!ownsSession(owner)) return;
      // Refresh token rejected (typically revoked server-side). Do the same
      // full session-expired cleanup the auto-logout branch performs, so the
      // user lands on /login with the "session expired" banner and no stale
      // React Query cache.
      doSessionExpiredLogout();
    } finally {
      if (ownsSession(owner)) setIsExtending(false);
    }
  }, [isExtending, updateTokens, doSessionExpiredLogout]);

  /**
   * Dismiss warning temporarily
   */
  const dismissWarning = useCallback(() => {
    setShowWarning(false);
    wasDismissedRef.current = true;
  }, []);

  /**
   * Check if we should show warning and handle auto-logout
   */
  useEffect(() => {
    if (!isAuthenticated) {
      setShowWarning(false);
      setTimeRemaining(0);
      hasAutoLoggedOutRef.current = false;
      wasDismissedRef.current = false;
      return;
    }

    const checkExpiration = () => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);

      // Auto-logout when timer reaches zero
      if (remaining <= 0 && !hasAutoLoggedOutRef.current) {
        hasAutoLoggedOutRef.current = true;
        setShowWarning(false);
        doSessionExpiredLogout();
        return;
      }

      // Show warning when within threshold (unless dismissed)
      if (remaining > 0 && remaining <= WARNING_THRESHOLD_MS) {
        if (!wasDismissedRef.current) {
          setShowWarning(true);
        }
      } else {
        // Reset dismissed flag when outside warning threshold
        wasDismissedRef.current = false;
        setShowWarning(false);
      }
    };

    // Check immediately
    checkExpiration();

    // Determine check interval based on whether warning is shown
    // Use faster interval when warning is visible for smooth countdown
    const intervalMs = showWarning ? COUNTDOWN_INTERVAL_MS : CHECK_INTERVAL_MS;
    const intervalId = setInterval(checkExpiration, intervalMs);

    return () => {
      clearInterval(intervalId);
    };
  }, [isAuthenticated, showWarning, calculateTimeRemaining, doSessionExpiredLogout]);

  return {
    showWarning,
    timeRemaining,
    formattedTime: formatTime(timeRemaining),
    extendSession,
    dismissWarning,
    isExtending,
  };
};
